// The minimal in-match canvas route (design D11a / DD2 / DD3). Since the composition
// root (`SessionController`) now owns the one session-scoped `Connection` and wires
// its world callback into the store, this view no longer opens, owns, or tears down a
// connection: it *consumes* the root-owned session (`getMatchSession`, never
// `getOrCreateMatchSession`) and renders purely from a `useClientStore.subscribe(
// currentWorld)` subscription — the same store-driven pattern `Match.tsx` uses (DD3).
// Refactored from the pre-D-071 shape that constructed its own `Connection` and drove
// `onWorld`/LOD callbacks itself (session-handoff task 2.4).
//
// The renderer seam stays injectable so `InMatchCanvas.test.tsx` can drive it with a
// recording fake renderer, server-free and GPU-free; production supplies the real
// PixiJS `CanvasRenderer` via the root's `createRenderer` seam.
//
// `camera` is a REACTIVE prop (camera-pan-zoom, design.md D4 / architecture-review F3):
// a change redraws through the SAME renderer instance — never recreating it — so a live
// pan/zoom (the Colony screen's controlled camera) can drive the rendered view without
// remounting the whole renderer. Additive: a caller whose `camera` never changes (the
// existing `/play` route) sees no extra draw beyond the one this always did.

import { useEffect, useRef } from 'react';
import { ESTABLISHING_CAMERA, type Camera } from '../canvas/projection';
import { CanvasRenderer } from '../canvas/renderer';
import { renderInputEquals, type RenderInput } from '../canvas/render-input';
import type { WorldSnapshot } from '../net/wire/connection';
import { getMatchSession, type MatchSessionKey } from '../session/match-session';
import { selectRenderInput, useClientStore } from '../state/store';
import { SIM_TICK_RATE_HZ } from '../generated/sim-constants';

/** The subset of `CanvasRenderer`'s API this component wires to — a real
 * `CanvasRenderer` satisfies it; tests inject a lightweight recorder instead (a real
 * PixiJS `Application` needs a GPU context `renderer.test.ts` deliberately avoids, per
 * design D10). `renderInput` is the additive render-input context (design D9) the call
 * site feeds from the store selectors; optional so a pre-change caller stays valid and
 * the renderer defaults to a live-play context. */
export interface InMatchRenderer {
  /** `signal` cancels a superseded construction (D-290) — a cancelled `init()` reclaims what it
   * has built and RESOLVES, so cancellation is never reported as a start-up failure. Optional,
   * which is what keeps every existing double that declares `init(): Promise<void>` assignable. */
  init(signal?: AbortSignal): Promise<void>;
  render(
    world: WorldSnapshot,
    camera: Camera,
    elapsedMs: number,
    tierChanged: boolean,
    renderInput?: RenderInput,
  ): void;
  /** Push an independent (non-world) render-input change so it reaches the renderer within
   * one frame even while the world stream stalls (design D9 / implementation-review F13):
   * season / anchor / lifecycle / epoch counters arrive off other messages, so a store
   * update carrying no world delta must still update the renderer's live render-input —
   * without re-ingesting a world (which would age the facing TTL as if a close frame
   * arrived). */
  updateRenderInput(renderInput: RenderInput): void;
  destroy(): void;
}

export interface InMatchCanvasDeps {
  /** Identifies the root-owned session this view renders from (design DD2). The view
   * looks it up read-only via `getMatchSession` — it never creates one. */
  sessionKey: MatchSessionKey;
  /**
   * `tickRateHz` is **wire-required** (client-tick-rate-provenance, tick-rate-single-source
   * E4): the factory takes the running rate as a required second parameter, sourced by this
   * component from `Welcome.manifest.tickRateHz` at the point it actually constructs the
   * renderer — no defaulted argument, no `?? 15`.
   */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  camera?: Camera;
  /** The tapped bee's id (bee-inspect-panel selection), merged into every render input this
   * host builds (design D9 `RenderInput.selectedBeeId`) — a view-state prop, not store state,
   * since a per-viewing inspection belongs to the caller (Colony.tsx), not the global store. */
  selectedBeeId?: number | null;
}


function defaultCreateRenderer(container: HTMLElement, tickRateHz: number): InMatchRenderer {
  return new CanvasRenderer(container, tickRateHz);
}

export function InMatchCanvas(deps: InMatchCanvasDeps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // `camera` is read fresh into a ref on every render (camera-pan-zoom, design.md D4):
  // the mount effect below never depends on it directly, so a caller's controlled-camera
  // state update never rebuilds the renderer — only the second, reactive-redraw effect
  // (further down) reacts to it.
  const cameraRef = useRef<Camera>(deps.camera ?? ESTABLISHING_CAMERA);
  cameraRef.current = deps.camera ?? ESTABLISHING_CAMERA;
  // `selectedBeeId` is read fresh into a ref on every render for the same reason `camera` is:
  // a caller's selection-state update (a tap) must never rebuild the renderer, only redraw
  // through it — see the reactive-redraw effect further down.
  const selectedBeeIdRef = useRef<number | null>(deps.selectedBeeId ?? null);
  selectedBeeIdRef.current = deps.selectedBeeId ?? null;
  // A torn-down invocation nulls `rendererRef`, so the null check IS the disposal guard —
  // there is deliberately no component-level `disposed` flag to fall out of step with it
  // (the ReviewFrame precedent, WAG-31/WAG-32). Cancellation is a per-invocation LOCAL in the
  // mount effect below.
  const rendererRef = useRef<InMatchRenderer | null>(null);
  const lastRenderTimeRef = useRef<number | null>(null);
  const drawFrameRef = useRef<(world: WorldSnapshot) => void>(() => undefined);
  // The wire rate as a PRIMITIVE subscription — see the carve-out note in the effect below.
  // `SIM_TICK_RATE_HZ` is the pre-handshake fallback and changes at most once, when Welcome
  // publishes the real rate, so this drives one renderer rebuild rather than per-render churn.
  const wireTickRateHz = useClientStore((s) => s.manifest?.tickRateHz ?? SIM_TICK_RATE_HZ);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    // Consume the root-owned session (design DD2): render only when the composition
    // root has installed a session for this key — never open one here.
    const session = getMatchSession(deps.sessionKey);
    if (session === undefined) {
      return;
    }

    const createRenderer = deps.createRenderer ?? defaultCreateRenderer;

    // **wire-required** (client-tick-rate-provenance, tick-rate-single-source E4), sourced
    // from `Welcome.manifest.tickRateHz`. This host is shared by two callers: the Colony
    // underlay, whose `ownHive` precondition (`ColonyUnderlaySeams`) guarantees Welcome has
    // already landed and `manifest` is real by the time it mounts here; and the standalone
    // `/play` scaffold route, which nothing in the app navigates to and which can mount before
    // any handshake — for that one instant this takes the **pre-handshake render** carve-out
    // (client-tick-rate-provenance's third fallback-permitted case) rather than inventing a
    // wire value that was never published. Once a real `manifest` exists it is never ignored —
    // which required this to become a reactive read with a dependency, not a `getState()`
    // snapshot under `[]` deps. It was the latter, so the `/play` mount pinned the mirror's rate
    // for the whole mount and no later Welcome could displace it (`Match.tsx` had the same gap).
    // The rate is a `private readonly` constructor argument to `PresentationClock` and
    // `CorpseTracker`, so following a late rate means rebuilding the renderer.
    const tickRateHz = wireTickRateHz;

    // Merges the current selection (a prop, so it never rides the store) onto a store-built
    // render input — used at every seam that hands one to the renderer, both the world-driven
    // `drawFrame` below and the no-world-delta `updateRenderInput` push, so a selection can
    // never be silently overwritten by an unrelated render-input push. Leaves `base` untouched
    // when nothing is selected — never appending an explicit `selectedBeeId: null` — so a caller
    // that never selects anything keeps the exact backward-compatible shape
    // (`client-rendering`: "existing render call sites keep working without the new inputs").
    const withSelection = (base: RenderInput): RenderInput =>
      selectedBeeIdRef.current === null ? base : { ...base, selectedBeeId: selectedBeeIdRef.current };

    function drawFrame(world: WorldSnapshot): void {
      const renderer = rendererRef.current;
      if (renderer === null) {
        return;
      }
      const now = performance.now();
      const elapsedMs = lastRenderTimeRef.current === null ? 0 : now - lastRenderTimeRef.current;
      lastRenderTimeRef.current = now;
      // The root's `onWorld` already advanced the confirmed tier (DD3); this route
      // just draws the latest confirmed world (no crossfade signal — matching
      // `Match.tsx`'s store-driven renderer). The render-input context (design D9) is
      // fed from the store selectors at this call site — the shared canvas host for both
      // the /play view and the /colony underlay — so canvas internals never read the
      // store; it defaults to the live-play context when no anchor/season slice is set.
      const renderInput = withSelection(selectRenderInput(useClientStore.getState()));
      // **A draw failure must never escape this function.** Every caller is somewhere it would
      // do collateral damage: the store subscription below runs inside zustand's
      // `listeners.forEach`, which has no guard of its own — a throw there skips every
      // REMAINING listener (including the composition root's own navigation/persistence
      // subscription, so an elimination or match-end would never be acted on) and propagates
      // back up through `Connection.applyOrGap`, which calls `onWorld` *before* `sendAck()`,
      // so the ack for that delta is never sent either. A broken renderer is a broken canvas;
      // it must not also cost the client its wire acks and its match lifecycle. Reported
      // rather than swallowed, and the renderer is left published — a draw failure is not a
      // start-up failure, and the next frame may well succeed.
      try {
        renderer.render(world, cameraRef.current, elapsedMs, false, renderInput);
      } catch (drawError: unknown) {
        console.error('waggle: the in-match canvas renderer failed to draw a frame', drawError);
      }
    }
    drawFrameRef.current = drawFrame;

    // Cancellation is **per invocation**, captured by this closure. It used to be the
    // component-level `disposedRef`, which cannot tell "this invocation was superseded" from
    // "a later invocation is alive": under `StrictMode` (which is how `mountApp` mounts) the
    // effect runs mount → cleanup → mount, and the second setup reset the shared flag back to
    // `false` before either `init()` settled — so the superseded construction both escaped
    // destruction and published ITSELF over `rendererRef` when it settled. That left a second,
    // orphaned canvas in the container — a leaked Pixi application, WebGL context and RAF
    // loop — and, whenever the superseded one settled last (the warm-asset-cache case, i.e.
    // the second visit to a screen), pointed every subsequent frame at the canvas clipped
    // below the stale one: a Colony field frozen on its first frame until a page reload.
    const built = createRenderer(container, tickRateHz);
    let cancelled = false;
    // The renderer's OWN cancellation channel (**D-290**), per invocation exactly as `cancelled`
    // is — and the two answer different questions. `cancelled` tells this closure it was
    // superseded; the controller tells the RENDERER, the only party that can give back a canvas
    // and a GPU context it has not finished attaching yet. Without it the teardown below reclaims
    // only what exists at that instant, and a construction still inside `init()` carries on to
    // attach a canvas afterwards — held for the whole sprite load, and for ever if it hangs.
    const controller = new AbortController();
    // Teardown is **immediate and exactly-once per call site**, whatever stage `init()` has
    // reached, because `CanvasRenderer.init()` appends its canvas and only *then* awaits
    // `assets.load()`: a superseded invocation owns an attached canvas and a live context for
    // the whole of that await. `CanvasRenderer.destroy()` guards on `this.app.renderer` and is
    // both partial-init tolerant and idempotent (`client-rendering`: "`init`/`destroy` MUST be
    // idempotent"), which is what lets the cancelled path run it twice — once at cleanup to
    // give back what exists then, once when `init()` settles to collect anything it attached
    // afterwards.
    //
    // That second call is now **belt to the abort's braces** rather than the mechanism: a
    // renderer that honours the signal has already unwound before it resolves. It is kept because
    // idempotence makes it free, and because this seam accepts doubles that ignore the signal
    // entirely — the parameter is optional. Dropping it is D-290's remedy's second half, whose
    // gate (this freeze fix landing) has now opened; it belongs to its own change rather than to
    // a merge resolution.
    const teardown = (): void => {
      try {
        built.destroy();
      } catch (teardownError: unknown) {
        console.error('waggle: the in-match canvas renderer failed to tear down', teardownError);
      }
    };
    void built.init(controller.signal).then(
      () => {
        if (cancelled) {
          // Ordinarily already destroyed by this invocation's cleanup; idempotence makes the
          // late-settling case a no-op rather than a double teardown.
          teardown();
          return;
        }
        rendererRef.current = built;
        // A keyframe/delta may have already landed in the store before init resolved.
        const world = useClientStore.getState().currentWorld;
        if (world !== null) {
          // `drawFrame` reports its own failures and never throws (see its guard above), so the
          // first draw cannot turn into an unhandled rejection on this promise chain — and a
          // trailing `.catch` here would be actively wrong, since it could not tell a draw
          // failure from the cancelled path's own `teardown()` throwing.
          drawFrame(world);
        }
      },
      // A renderer that cannot start (no WebGL context, a failed sheet load) is still torn
      // down: `init()` appends its canvas before the load it rejected on, so a rejection is
      // never "nothing was built". Two-argument `then`, not a trailing `.catch` — a chained
      // catch would also catch a throw from the fulfilment handler and destroy a renderer
      // already published to `rendererRef`.
      //
      // `init()` now unwinds its own half-built state before rejecting (D-290), so this call is
      // the same idempotent second teardown as the cancelled path's. **A cancellation never
      // reaches here**: a cancelled `init()` resolves, precisely so ordinary teardown is not
      // reported as a start-up failure.
      (error: unknown) => {
        teardown();
        console.error('waggle: the in-match canvas renderer failed to start', error);
      },
    );

    // Redraw on every confirmed world update, keyed on EITHER a `currentWorld`
    // reference change OR an advancing `lastSeq` (both driven by the root's `onWorld`) —
    // neither alone suffices (implementation-review F1; mirrors the `Match` screen
    // subscription): the `Connection` mutates one `WorldSnapshot` in place across a
    // delta stream (stable reference, advancing seq — the seq clause catches those),
    // while a fresh/reconnect keyframe installs a NEW reference whose per-connection
    // sequence can reuse the retained `lastSeq` (both 0 after a server-side seq reset —
    // the reference clause catches that, so a reconnect can't freeze on a stale frame).
    const unsubscribe = useClientStore.subscribe((state, prev) => {
      if (rendererRef.current === null) {
        return;
      }
      if (
        state.currentWorld !== null &&
        (state.currentWorld !== prev.currentWorld || state.lastSeq !== prev.lastSeq)
      ) {
        // A world update: `drawFrame` reads the latest render-input too.
        drawFrame(state.currentWorld);
        return;
      }
      // No world delta this update, but an independent render-input signal (season / anchor /
      // lifecycle / epoch) may have changed off another message. Push it so the RAF loop
      // reflects it within one frame — an elimination-only lifecycle flip clears the anchor
      // even when the stream has stalled (implementation-review F13). `updateRenderInput` does
      // NOT re-ingest a world, so it cannot age the facing TTL.
      const nextInput = withSelection(selectRenderInput(state));
      if (!renderInputEquals(nextInput, withSelection(selectRenderInput(prev)))) {
        // Guarded for the same reason `drawFrame` is: this runs inside zustand's unguarded
        // `listeners.forEach`, so a throw here would take out every remaining subscriber and
        // the delta's ack with it.
        try {
          rendererRef.current.updateRenderInput(nextInput);
        } catch (pushError: unknown) {
          console.error('waggle: the in-match canvas renderer failed to take a render input', pushError);
        }
      }
    });

    return () => {
      cancelled = true;
      // Tell the renderer too (D-290): a construction still inside `init()` gives its canvas and
      // its GPU context back AT ONCE on this, rather than whenever its sprite load settles.
      controller.abort();
      unsubscribe();
      lastRenderTimeRef.current = null;
      // Only ever relinquish the ref if THIS invocation is the one holding it — a later
      // invocation's renderer must survive this one's teardown.
      if (rendererRef.current === built) {
        rendererRef.current = null;
      }
      // Unconditional: an invocation torn down mid-`init()` must give its canvas and context
      // back now, not whenever its asset load happens to settle.
      teardown();
    };
    // `deps` is a stable DI seam (injected once by the caller — `sessionKey`/
    // `createRenderer` are not guaranteed referentially stable across re-renders at
    // every call site, e.g. an inline `createRenderer={() => ...}`), not reactive props —
    // one renderer per mount, the session lifecycle belongs to the root. `deps.camera` is
    // handled by the separate reactive-redraw effect below, which never rebuilds the
    // renderer.
    //
    // `wireTickRateHz` is the one exception, and it is deliberate: the rate cannot be updated on
    // a live renderer (constructor-only), so honouring a Welcome that lands after this mount
    // requires a rebuild. One primitive, one transition, one rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wireTickRateHz]);

  // The reactive-camera redraw (design.md D4, architecture-review F3): a `camera`-prop
  // change (a new object identity — the caller's controlled-camera state update, e.g.
  // Colony's pan/zoom) redraws the CURRENT world through the SAME renderer instance via
  // the ref-hoisted `drawFrame`, without touching the mount effect above at all. A no-op
  // when no renderer/world exists yet (pre-init, or the underlay not yet mounted) or on
  // the very first render (the mount effect's own `init().then()` already draws once).
  //
  // `deps.selectedBeeId` rides the same dependency array (bee-selection-outline): a tap that
  // changes the selection but lands on the same world frame — no world delta in between —
  // still needs a redraw, and this effect already exists for exactly this shape of problem.
  useEffect(() => {
    if (rendererRef.current === null) {
      return;
    }
    const world = useClientStore.getState().currentWorld;
    if (world !== null) {
      drawFrameRef.current(world);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps.camera, deps.selectedBeeId]);

  return <div ref={containerRef} data-testid="in-match-canvas" style={{ width: '100%', height: '100%' }} />;
}
